import React, { useState, useEffect, useRef } from "react";
import classes from "./ReusableTable.module.css";
import { IoEyeOutline } from "react-icons/io5";
import { NavLink } from "react-router-dom";
import CommonButton from "../ui/CommonButton/CommonButton";
import { FiUser } from "react-icons/fi";
import { env } from "../../constants/env";
import { toast } from "react-toastify";
import { useAuth } from "../../context/AuthContext";
import { MdOutlineFileDownload } from "react-icons/md";
import { AiOutlineEdit, AiOutlineSafetyCertificate } from "react-icons/ai";
import { GoGear } from "react-icons/go";
import { LuPackage, LuClock8 } from "react-icons/lu";
import { LiaCertificateSolid } from "react-icons/lia";
import { RiDeleteBinLine } from "react-icons/ri";

const ReusableTable = ({
  columns,
  data,
  title,
  showDetails = true,
  onAdd,
  showTopAdd,
  className,
  onEdit,
  accreditationName,
  showActions = true,
  onDelete,
  useOverlay,
  onShowDetails,
  zammasTable = false,
}) => {
  const [openMenuIndex, setOpenMenuIndex] = useState(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const [popupData, setPopupData] = useState(null);
  const tableRef = useRef(null);
  const buttonRefs = useRef([]);
  const { token, user } = useAuth();

  console.log("Looking at the data", data);

  const userRole = user?.role;

  const downloadReport = async (labId, accreditationName) => {
    try {
      let url;
      if (accreditationName.toLowerCase() === "slipta") {
        url = `${env.kataApiUrl}/api/certification/${labId}/download-report`;
      } else {
        url = `${env.kataApiUrl}/api/accreditation/${labId}/${accreditationName}/download-report`;
      }

      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) throw new Error("Failed to download report");

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = downloadUrl;

      const filename =
        accreditationName.toLowerCase() === "slipta"
          ? `Certification_Report_${labId}.xlsx`
          : `Accreditation_Report_${accreditationName}_${labId}.xlsx`;

      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();

      window.URL.revokeObjectURL(downloadUrl);
    } catch (err) {
      toast.error(err.message || "Download failed");
    }
  };

  const toggleMenu = (index, e) => {
    e.stopPropagation();
    const button = buttonRefs.current[`action-${index}`];
    const buttonRect = button.getBoundingClientRect();
    const tableRect = tableRef.current.getBoundingClientRect();

    setMenuPosition({
      top: buttonRect.bottom - tableRect.top,
      left: buttonRect.right - tableRect.left - 155,
    });

    setOpenMenuIndex(openMenuIndex === index ? null : index);
  };

  const openPopup = (rowIndex, colField, e, details) => {
    e.stopPropagation();
    const button = buttonRefs.current[`${rowIndex}-${colField}`];
    const buttonRect = button.getBoundingClientRect();
    const tableRect = tableRef.current.getBoundingClientRect();

    setPopupData({
      top: buttonRect.bottom - tableRect.top + 5,
      left: buttonRect.left - tableRect.left,
      details,
      colField,
    });
  };

  useEffect(() => {
    const handleClickOutside = () => {
      setOpenMenuIndex(null);
      setPopupData(null);
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  const renderPopupContent = () => {
    if (!popupData) return null;

    const report = popupData.details;

    console.log("I want to see report", popupData);

    switch (popupData.colField) {
      case "reagentStock":
        return (
          <>
            <h5 className={classes.popupTitle}>
              <LuPackage /> Reagent Stock Status
            </h5>
            {report.stockMonths &&
            Object.keys(report.stockMonths).length > 0 ? (
              Object.keys(report.stockMonths).map((test, i) => (
                <div
                  key={i}
                  className={`${classes.detailRow} d-flex align-items-center justify-content-between gap-2`}
                >
                  <h4>{test}</h4>

                  {/* Only render if kitsRemaining exists */}
                  {report.kitsRemaining?.[test] != null && (
                    <div className={classes.kitsRemaining}>
                      {report.kitsRemaining[test]} kits remaining
                    </div>
                  )}

                  {/* Only render if stockMonths exists */}
                  {report.stockMonths?.[test] != null && (
                    <div className={classes.stockMonths}>
                      {report.stockMonths[test]} months
                    </div>
                  )}
                </div>
              ))
            ) : (
              <div>No stock data available</div>
            )}
          </>
        );
      case "equipmentStatus":
        return (
          <>
            <h5 className={classes.popupTitle}>
              <GoGear /> Equipment Status
            </h5>
            {Object.keys(report.equipmentStatus).map((test, i) => (
              <div
                key={i}
                className={`${classes.detailRow} d-flex align-items-center justify-content-between gap-2`}
              >
                <h4>{report.availableInstrument?.[test] || test}</h4>
                <span
                  className={
                    report.equipmentStatus[test] === "Functional"
                      ? classes.goodBadge
                      : classes.badBadge
                  }
                >
                  {report.equipmentStatus[test]}
                </span>
              </div>
            ))}
          </>
        );
      case "sampleBacklog":
        return (
          <>
            <h5 className={classes.popupTitle}>
              <LuClock8 /> Sample Backlog
            </h5>
            {Object.keys(report.backlog).map((test, i) => (
              <div
                key={i}
                className={`${classes.detailRow} d-flex align-items-center justify-content-between gap-2`}
              >
                <h4>{test}</h4>
                <span className={classes.pendingBadge}>
                  {report.backlog[test]} pending
                </span>
              </div>
            ))}
          </>
        );
      default:
        return null;
    }
  };

  return (
    <div className={`${classes.tableWrapper} ${className}`} ref={tableRef}>
      <div className="d-flex align-items-center justify-content-between mb-3">
        {title ? <h3 className={classes.tableTitle}>{title}</h3> : <div></div>}
        {showTopAdd && (
          <CommonButton
            title="Add new"
            onClick={onAdd}
            className={classes.addButton}
          />
        )}
      </div>
      <div className={classes.tableContainer}>
        <table className={classes.customTable}>
          <thead className={classes.tableHead}>
            <tr>
              {columns.map((col, idx) => (
                <th key={idx} className={col.headCellClassName || ""}>
                  {col.label}
                </th>
              ))}
              {showActions && <th>Actions</th>}
            </tr>
          </thead>
          <tbody className={classes.tableBody}>
            {data.length > 0 ? (
              data.map((row, rowIndex) => (
                <tr key={rowIndex} className={classes.tableRow}>
                  {columns.map((col, colIndex) => {
                    // Popups
                    if (
                      [
                        "reagentStock",
                        "equipmentStatus",
                        "sampleBacklog",
                      ].includes(col.field)
                    ) {
                      const hasData =
                        (col.field === "reagentStock" &&
                          row.latestWeeklyReport?.kitsRemaining) ||
                        (col.field === "reagentStock" &&
                          row.latestWeeklyReport?.stockMonths) ||
                        (col.field === "equipmentStatus" &&
                          row.latestWeeklyReport?.equipmentStatus) ||
                        (col.field === "sampleBacklog" &&
                          row.latestWeeklyReport?.backlog);

                      return (
                        <td key={colIndex}>
                          {hasData && (
                            <button
                              ref={(el) =>
                                (buttonRefs.current[
                                  `${rowIndex}-${col.field}`
                                ] = el)
                              }
                              className={classes.viewButton}
                              onClick={(e) =>
                                openPopup(
                                  rowIndex,
                                  col.field,
                                  e,
                                  row.latestWeeklyReport
                                )
                              }
                            >
                              {col.field === "equipmentStatus" && (
                                <GoGear style={{ marginRight: "5px" }} />
                              )}
                              {col.field === "reagentStock" && (
                                <LuPackage style={{ marginRight: "5px" }} />
                              )}
                              {col.field === "sampleBacklog" && (
                                <LuClock8 style={{ marginRight: "5px" }} />
                              )}
                              VIEW
                            </button>
                          )}
                          {!hasData && "unknown"}
                        </td>
                      );
                    }

                    // Status
                    if (col.field === "status") {
                      // Default to true if useStatusPill is undefined
                      const usePill =
                        col.useStatusPill !== undefined
                          ? col.useStatusPill
                          : true;

                      if (usePill) {
                        let bgColor = "";
                        switch (row[col.field]) {
                          case "Poor":
                            bgColor = "rgba(186, 55, 55, 0.2)";
                            break;
                          case "Fair":
                            bgColor = "rgba(186, 125, 55, 0.2)";
                            break;
                          case "Good":
                            bgColor = "rgba(19, 2, 251, 0.2)";
                            break;
                          case "Excellent":
                            bgColor = "rgba(28, 223, 3, 0.2)";
                            break;
                          default:
                            bgColor = "transparent";
                        }
                        return (
                          <td key={colIndex}>
                            <span
                              className={classes.statusPill}
                              style={{ backgroundColor: bgColor }}
                            >
                              {row[col.field] ?? "unknown"}
                            </span>
                          </td>
                        );
                      } else {
                        // Plain text
                        return (
                          <td
                            key={colIndex}
                            className={col.cellClassName || ""}
                          >
                            {row[col.field] ?? "unknown"}
                          </td>
                        );
                      }
                    }

                    // Certifications
                    if (col.field === "slipta") {
                      return (
                        <td key={colIndex}>
                          {row.slipta?.nextDue ? (
                            <div className={classes.sliptapill}>
                              <LiaCertificateSolid size={15} color="#1CDF03" />
                              Certified
                            </div>
                          ) : (
                            "unknown"
                          )}
                        </td>
                      );
                    }

                    if (col.field === "sadcas") {
                      return (
                        <td key={colIndex}>
                          {row.sadcas?.type ? (
                            <div className={classes.sadcaspill}>
                              <LiaCertificateSolid
                                size={15}
                                color="var(--primary-color)"
                              />{" "}
                              {row.sadcas.type}
                            </div>
                          ) : (
                            "unknown"
                          )}
                        </td>
                      );
                    }

                    if (col.field === "hpcz") {
                      return (
                        <td key={colIndex}>
                          {row.hpcz?.expiryDate ? (
                            <div className={classes.hpczpill}>
                              <AiOutlineSafetyCertificate
                                size={15}
                                color="#C607CA"
                              />
                              Licensed
                            </div>
                          ) : (
                            "unknown"
                          )}
                        </td>
                      );
                    }

                    // Staff field
                    if (col.field === "staff") {
                      return (
                        <td key={colIndex}>
                          <FiUser style={{ marginRight: "5px" }} />
                          {row[col.field] ?? "unknown"}
                        </td>
                      );
                    }

                    // Default fallback for all other fields
                    return (
                      <td key={colIndex} className={col.cellClassName || ""}>
                        {col.cellRenderer
                          ? col.cellRenderer(row)
                          : row[col.field] ?? "unknown"}
                      </td>
                    );
                  })}

                  {showActions && (
                    <td className={classes.actionCell}>
                      <button
                        ref={(el) =>
                          (buttonRefs.current[`action-${rowIndex}`] = el)
                        }
                        className={classes.dotButton}
                        onClick={(e) => toggleMenu(rowIndex, e)}
                      >
                        ⋮
                      </button>
                    </td>
                  )}
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan={columns.length + (showActions ? 1 : 0)}
                  className={classes.noDataMessage}
                >
                  You have no rows... yet
                  {onAdd && (
                    <div className="d-flex align-items-center justify-content-center mt-4">
                      <CommonButton
                        title="Add new"
                        onClick={onAdd}
                        className={classes.addButton}
                      />
                    </div>
                  )}
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {/* Action menu */}
        {showActions && openMenuIndex !== null && (
          <div
            className={classes.popupMenu}
            style={{
              position: "absolute",
              top: `${menuPosition.top}px`,
              left: `${menuPosition.left}px`,
              marginTop: "-1.5rem",
            }}
          >
            {userRole === "MINISTRY" && accreditationName ? (
              <button
                className={classes.popupItem}
                onClick={() =>
                  downloadReport(data[openMenuIndex].id, accreditationName)
                }
              >
                <MdOutlineFileDownload
                  className={classes.popupIcon}
                  size={15}
                />
                <span>Download Report</span>
              </button>
            ) : (
              <>
                {showDetails &&
                  (useOverlay ? (
                    <button
                      className={classes.popupItem}
                      onClick={() => onShowDetails(data[openMenuIndex])}
                    >
                      <IoEyeOutline className={classes.popupIcon} size={15} />
                      <span>Show Details</span>
                    </button>
                  ) : (
                    <NavLink
                      to={data[openMenuIndex].id}
                      className={classes.popupItem}
                    >
                      <IoEyeOutline className={classes.popupIcon} size={15} />
                      <span>Show Details</span>
                    </NavLink>
                  ))}
              </>
            )}

            {((onEdit && userRole === "OWNER") ||
              (onEdit && userRole === "ASSESSOR") ||
              (onEdit && userRole === "MINISTRY") ||
              (onEdit && userRole === "ZAMMSA" && zammasTable) ||
              (onEdit && userRole === "LABORATORY" && zammasTable) ||
              (onEdit && userRole === "HEALTH CENTER" && zammasTable)) && (
              <button
                className={classes.popupItem}
                onClick={() => onEdit(data[openMenuIndex])}
              >
                <AiOutlineEdit className={classes.popupIcon} size={15} />
                <span>Edit</span>
              </button>
            )}

            {((onDelete && userRole === "OWNER") ||
              (onDelete && userRole === "MINISTRY") ||
              (onDelete && userRole === "ZAMMSA" && zammasTable)) && (
              <button
                className={`${classes.popupItem} ${classes.deleteButton}`}
                onClick={() => onDelete(data[openMenuIndex])}
              >
                <RiDeleteBinLine size={15} /> Delete
              </button>
            )}
          </div>
        )}

        {/* Detail popup */}
        {popupData && (
          <div
            className={classes.detailPopup}
            style={{ top: `${popupData.top}px`, left: `${popupData.left}px` }}
            onClick={(e) => e.stopPropagation()}
          >
            {renderPopupContent()}
          </div>
        )}
      </div>
    </div>
  );
};

export default ReusableTable;
